/**
 * 親犬マッチングロジック
 * PDF解析結果の親犬情報と既存の犬データをマッチングする
 */

import { Dog, Gender } from '../types/dog';
import {
  normalizeDogName,
  normalizeRegistrationNumber,
  calculateStringSimilarity,
} from './nameNormalizer';

/**
 * マッチ結果の種類
 */
export type ParentMatchType =
  | 'exact_registration' // JKC/DNA登録番号完全一致
  | 'exact_name' // 名前完全一致
  | 'partial_name' // 名前部分一致
  | 'fuzzy_name' // 名前類似度マッチ
  | 'none'; // マッチなし

/**
 * 親犬マッチング結果
 */
export interface ParentMatchResult {
  /** マッチした犬のID。マッチなしの場合null */
  matchedDogId: string | null;
  /** 信頼度 0-100 */
  confidence: number;
  /** マッチの種類 */
  matchType: ParentMatchType;
  /** マッチ候補リスト（信頼度順） */
  candidates: Array<{
    dogId: string;
    dog: Dog;
    confidence: number;
    matchType: ParentMatchType;
  }>;
}

/**
 * 親犬情報（PDF解析結果から）
 */
export interface ParentInfo {
  name?: string;
  registrationNumber?: string;
}

/**
 * 閾値定義
 */
const THRESHOLDS = {
  EXACT_REGISTRATION_CONFIDENCE: 100,
  EXACT_NAME_CONFIDENCE: 90,
  PARTIAL_NAME_CONFIDENCE: 80,
  FUZZY_MIN_SIMILARITY: 70,
  FUZZY_MAX_CONFIDENCE: 85,
  FUZZY_MIN_CONFIDENCE: 70,
};

/**
 * 登録番号でマッチングを試みる
 */
function matchByRegistrationNumber(
  registrationNumber: string,
  dogs: Dog[]
): { dog: Dog; matchType: ParentMatchType } | null {
  if (!registrationNumber) return null;

  const normalizedInput = normalizeRegistrationNumber(registrationNumber);

  for (const dog of dogs) {
    const jkcNormalized = normalizeRegistrationNumber(
      dog.jkcRegistrationNumber || ''
    );
    const dnaNormalized = normalizeRegistrationNumber(
      dog.dnaRegistrationNumber || ''
    );

    if (
      (jkcNormalized && jkcNormalized === normalizedInput) ||
      (dnaNormalized && dnaNormalized === normalizedInput)
    ) {
      return { dog, matchType: 'exact_registration' };
    }
  }

  return null;
}

/**
 * 名前で完全一致マッチングを試みる
 */
function matchByExactName(
  name: string,
  dogs: Dog[]
): { dog: Dog; matchType: ParentMatchType } | null {
  if (!name) return null;

  const normalizedInput = normalizeDogName(name);

  for (const dog of dogs) {
    const normalizedName = normalizeDogName(dog.name);
    const normalizedOfficialName = normalizeDogName(dog.officialName || '');

    if (
      normalizedName === normalizedInput ||
      (normalizedOfficialName && normalizedOfficialName === normalizedInput)
    ) {
      return { dog, matchType: 'exact_name' };
    }
  }

  return null;
}

/**
 * 名前で部分一致マッチングを試みる
 * 入力名に犬の名前または正式名称が含まれているか確認
 */
function matchByPartialName(
  name: string,
  dogs: Dog[]
): { dog: Dog; matchType: ParentMatchType } | null {
  if (!name) return null;

  const normalizedInput = normalizeDogName(name);

  for (const dog of dogs) {
    const normalizedName = normalizeDogName(dog.name);
    const normalizedOfficialName = normalizeDogName(dog.officialName || '');

    // 入力に犬の名前が含まれている場合
    if (
      normalizedInput.includes(normalizedName) ||
      (normalizedOfficialName &&
        normalizedInput.includes(normalizedOfficialName))
    ) {
      return { dog, matchType: 'partial_name' };
    }
  }

  return null;
}

/**
 * 名前のファジーマッチング（類似度ベース）
 */
function matchByFuzzyName(
  name: string,
  dogs: Dog[]
): Array<{
  dog: Dog;
  matchType: ParentMatchType;
  confidence: number;
}> {
  if (!name) return [];

  const normalizedInput = normalizeDogName(name);
  const results: Array<{
    dog: Dog;
    matchType: ParentMatchType;
    confidence: number;
  }> = [];

  for (const dog of dogs) {
    const normalizedName = normalizeDogName(dog.name);
    const normalizedOfficialName = normalizeDogName(dog.officialName || '');

    // 名前との類似度
    const nameSimilarity = calculateStringSimilarity(
      normalizedInput,
      normalizedName
    );
    // 正式名称との類似度
    const officialSimilarity = normalizedOfficialName
      ? calculateStringSimilarity(normalizedInput, normalizedOfficialName)
      : 0;

    const maxSimilarity = Math.max(nameSimilarity, officialSimilarity);

    if (maxSimilarity >= THRESHOLDS.FUZZY_MIN_SIMILARITY) {
      // 類似度70%以上で70-85%の信頼度にマッピング
      const confidence = Math.round(
        THRESHOLDS.FUZZY_MIN_CONFIDENCE +
          ((maxSimilarity - THRESHOLDS.FUZZY_MIN_SIMILARITY) /
            (100 - THRESHOLDS.FUZZY_MIN_SIMILARITY)) *
            (THRESHOLDS.FUZZY_MAX_CONFIDENCE - THRESHOLDS.FUZZY_MIN_CONFIDENCE)
      );

      results.push({
        dog,
        matchType: 'fuzzy_name',
        confidence: Math.min(confidence, THRESHOLDS.FUZZY_MAX_CONFIDENCE),
      });
    }
  }

  // 信頼度順にソート
  return results.sort((a, b) => b.confidence - a.confidence);
}

/**
 * 親犬マッチングを実行
 *
 * マッチング優先度:
 * 1. JKC/DNA登録番号完全一致 → 100%
 * 2. 名前完全一致 → 90%
 * 3. 名前部分一致（正式名称含む）→ 80%
 * 4. 名前類似度70%以上 → 70-85%
 */
export function findParentDogMatch(
  parentInfo: ParentInfo,
  dogs: Dog[],
  gender: Gender
): ParentMatchResult {
  const noMatch: ParentMatchResult = {
    matchedDogId: null,
    confidence: 0,
    matchType: 'none',
    candidates: [],
  };

  if (!parentInfo.name && !parentInfo.registrationNumber) {
    return noMatch;
  }

  // 性別でフィルタリング
  const filteredDogs = dogs.filter((dog) => dog.gender === gender);

  if (filteredDogs.length === 0) {
    return noMatch;
  }

  // 1. 登録番号でマッチング（最優先）
  if (parentInfo.registrationNumber) {
    const regMatch = matchByRegistrationNumber(
      parentInfo.registrationNumber,
      filteredDogs
    );
    if (regMatch) {
      return {
        matchedDogId: regMatch.dog.id,
        confidence: THRESHOLDS.EXACT_REGISTRATION_CONFIDENCE,
        matchType: regMatch.matchType,
        candidates: [
          {
            dogId: regMatch.dog.id,
            dog: regMatch.dog,
            confidence: THRESHOLDS.EXACT_REGISTRATION_CONFIDENCE,
            matchType: regMatch.matchType,
          },
        ],
      };
    }
  }

  // 2. 名前完全一致
  if (parentInfo.name) {
    const exactMatch = matchByExactName(parentInfo.name, filteredDogs);
    if (exactMatch) {
      return {
        matchedDogId: exactMatch.dog.id,
        confidence: THRESHOLDS.EXACT_NAME_CONFIDENCE,
        matchType: exactMatch.matchType,
        candidates: [
          {
            dogId: exactMatch.dog.id,
            dog: exactMatch.dog,
            confidence: THRESHOLDS.EXACT_NAME_CONFIDENCE,
            matchType: exactMatch.matchType,
          },
        ],
      };
    }

    // 3. 名前部分一致
    const partialMatch = matchByPartialName(parentInfo.name, filteredDogs);
    if (partialMatch) {
      return {
        matchedDogId: partialMatch.dog.id,
        confidence: THRESHOLDS.PARTIAL_NAME_CONFIDENCE,
        matchType: partialMatch.matchType,
        candidates: [
          {
            dogId: partialMatch.dog.id,
            dog: partialMatch.dog,
            confidence: THRESHOLDS.PARTIAL_NAME_CONFIDENCE,
            matchType: partialMatch.matchType,
          },
        ],
      };
    }

    // 4. ファジーマッチング
    const fuzzyMatches = matchByFuzzyName(parentInfo.name, filteredDogs);
    if (fuzzyMatches.length > 0) {
      const bestMatch = fuzzyMatches[0];
      return {
        matchedDogId: bestMatch.dog.id,
        confidence: bestMatch.confidence,
        matchType: bestMatch.matchType,
        candidates: fuzzyMatches.map((m) => ({
          dogId: m.dog.id,
          dog: m.dog,
          confidence: m.confidence,
          matchType: m.matchType,
        })),
      };
    }
  }

  return noMatch;
}
